import {
  getGetChannelsApiV1QueryKey,
  useGetChannelsApiV1Suspense,
  useRegisterInstagramChannelApiV1,
  useUnregisterInstagramChannelApiV1,
  useUnregisterTiktokChannelApiV1,
} from "@/api/__generated__/api.schema";
import { WorkspaceChannelPlatform } from "@/api/__generated__/models";
import { PageContainer } from "@/components/common/page-container";
import { ExternalLink } from "@/components/external-link";
import { Preload } from "@/components/preload";
import { useWorkspaceMemberRole } from "@/components/role/allowed";
import { appEnv } from "@/constants/app-env";
import { env } from "@/constants/env";
import { getAssetsUrl, routes } from "@/constants/url";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { cn } from "@/lib/utils";
import { openWindowPopup } from "@/utils/window";
import {
  Badge,
  Button,
  List,
  ListItem,
  ShadowBox,
  Thumbnail,
  ThumbnailImage,
  Typography,
  useConfirm,
  useModal,
  useToastMessage,
} from "@backpac/monica";
import { useQueryClient } from "@tanstack/react-query";
import { type ComponentProps, type ReactNode, useCallback } from "react";
import { ChannelTable } from "./components/channel-table";

export function ChannelsPage() {
  return (
    <PageContainer nav={<PageContainer.Navigation title="Social Accounts" />}>
      <Preload.Image
        fileNames={[
          "verification_step_1_desktop.png",
          "verification_step_1_mobile.png",
          "verification_step_2_desktop.png",
          "verification_step_2_mobile.png",
          "verification_step_3_desktop.png",
          "verification_step_3_mobile.png",
        ]}
      />
      <div className="space-y-6">
        {appEnv.isProduction ? null : <TiktokSection />}
        {appEnv.isProduction ? null : <InstagramSection />}
        <YoutubeSection />
      </div>
    </PageContainer>
  );
}

function useChannel({ platform }: { platform: WorkspaceChannelPlatform }) {
  const workspaceId = useWorkspaceId();
  const {
    data: {
      data: { items },
    },
  } = useGetChannelsApiV1Suspense(workspaceId);

  return {
    data: { item: items.find((item) => item.platform === platform) },
  };
}

function TiktokSection({ className }: { className?: string }) {
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();
  const { confirm } = useConfirm();
  const { info } = useToastMessage();
  const {
    data: { isOverAdmin },
  } = useWorkspaceMemberRole();
  const {
    data: { item: tiktok },
  } = useChannel({ platform: WorkspaceChannelPlatform.TIKTOK });

  const { mutate: unregisterTiktok, isPending: isUnregisteringTiktok } = useUnregisterTiktokChannelApiV1({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetChannelsApiV1QueryKey(workspaceId) });
      },
    },
  });

  if (tiktok == null) {
    return (
      <ChannelsPage.ConnectSection
        className={className}
        title="TikTok"
        description="Connect your TikTok account to Backpac"
        icon={getAssetsUrl("sns/tiktok.png")}
        rightAddon={<ConnectTiktok disabled={!isOverAdmin} />}
      />
    );
  }

  return (
    <ShadowBox className={cn("flex justify-between p-8 text-left", className)}>
      <div className="flex items-center gap-4">
        <Thumbnail>
          <ThumbnailImage src={tiktok.profileImageUrl} />
        </Thumbnail>
        <div className="flex flex-col gap-1">
          <Typography size="sm" weight={600}>
            {tiktok.name}
          </Typography>
          <Typography size="xs" color="textMuted">
            @{tiktok.customUrl}
          </Typography>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <Badge theme="green">Connected</Badge>
        <Button
          variant="outlined"
          loading={isUnregisteringTiktok}
          onClick={async () => {
            const confirmed = await confirm({
              title: "Disconnect TikTok",
              description: "Are you sure you want to disconnect your TikTok account?",
              confirmText: "Disconnect",
              confirmVariant: "error",
              cancelText: "Cancel",
            });

            if (confirmed) {
              unregisterTiktok(
                { workspaceId },
                {
                  onSuccess: () => {
                    info("TikTok disconnected successfully");
                  },
                },
              );
            }
          }}
        >
          Disconnect
        </Button>
      </div>
    </ShadowBox>
  );
}

function ConnectTiktok(props: ComponentProps<typeof Button>) {
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();

  return (
    <Button
      onClick={() =>
        authTiktok(workspaceId, {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getGetChannelsApiV1QueryKey(workspaceId) });
          },
        })
      }
      {...props}
    >
      Connect to TikTok
    </Button>
  );
}

function authTiktok(workspaceId: string, options?: { onSuccess?: () => void }) {
  const returnUrl = `${window.location.origin}${routes.OAUTH.tiktok}`;

  const searchParams = new URLSearchParams();
  searchParams.set("response_type", "code");
  searchParams.set("client_key", env.TIKTOK_CLIENT_KEY);
  searchParams.set("scope", "user.info.basic,user.info.profile,user.info.stats");
  searchParams.set("redirect_uri", returnUrl); // NOTE: we don't need to encodeURIComponent
  searchParams.set("disable_auto_auth", "1");
  searchParams.set("state", JSON.stringify({ workspaceId, returnUrl: encodeURIComponent(returnUrl) }));

  const tiktokAuthUrl = `https://www.tiktok.com/v2/auth/authorize/?${searchParams.toString()}`;

  openWindowPopup(tiktokAuthUrl, {
    title: "TikTok Authentication",
    width: 500,
    height: 600,
    onClose: options?.onSuccess,
  });
}

function InstagramSection({ className }: { className?: string }) {
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();
  const { info } = useToastMessage();
  const { confirm } = useConfirm();
  const {
    data: { isOverAdmin },
  } = useWorkspaceMemberRole();
  const {
    data: { item: instagram },
  } = useChannel({ platform: WorkspaceChannelPlatform.INSTAGRAM });
  const { mutate: unregisterInstagram, isPending: isUnregisteringInstagram } = useUnregisterInstagramChannelApiV1({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetChannelsApiV1QueryKey(workspaceId) });
      },
    },
  });

  if (instagram == null) {
    return (
      <ChannelsPage.ConnectSection
        className={className}
        title="Instagram"
        description="Connect your Instagram account to Backpac"
        icon={getAssetsUrl("sns/instagram.png")}
        subDescription={<InstagramRequirements />}
        rightAddon={<ConnectInstagramButton disabled={!isOverAdmin} />}
      />
    );
  }

  return (
    <ShadowBox className={cn("flex justify-between p-8 text-left", className)}>
      <div className="flex items-center gap-4">
        <Thumbnail>
          <ThumbnailImage src={instagram.profileImageUrl} />
        </Thumbnail>
        <div className="flex flex-col gap-1">
          <Typography size="sm" weight={600}>
            {instagram.name}
          </Typography>
          <Typography size="xs" color="textMuted">
            @{instagram.customUrl}
          </Typography>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <Badge theme="green">Connected</Badge>
        <Button
          variant="outlined"
          loading={isUnregisteringInstagram}
          onClick={async () => {
            const confirmed = await confirm({
              title: "Disconnect Instagram",
              description: "Are you sure you want to disconnect your Instagram account?",
              confirmText: "Disconnect",
              confirmVariant: "error",
              cancelText: "Cancel",
            });

            if (confirmed) {
              unregisterInstagram(
                { workspaceId },
                {
                  onSuccess: () => {
                    info("Instagram disconnected successfully");
                  },
                },
              );
            }
          }}
        >
          Disconnect
        </Button>
      </div>
    </ShadowBox>
  );
}

function ConnectInstagramButton(props: ComponentProps<typeof Button>) {
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();
  const { info } = useToastMessage();
  const { mutate: registerInstagram, isPending: isRegisteringInstagram } = useRegisterInstagramChannelApiV1({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetChannelsApiV1QueryKey(workspaceId) });
      },
    },
  });

  const handleClientLogin = useCallback(async () => {
    const accessToken = await authInstagram();

    registerInstagram(
      { workspaceId, data: { accessToken } },
      {
        onSuccess: () => {
          info("Instagram connected successfully");
        },
      },
    );
  }, [workspaceId, registerInstagram, info]);

  return (
    <Button onClick={handleClientLogin} loading={isRegisteringInstagram} {...props}>
      Connect to Instagram
    </Button>
  );
}

function authInstagram() {
  return new Promise<string>((resolve) => {
    window.FB.login(
      (response) => {
        const accessToken = response.authResponse?.accessToken;

        if (accessToken != null) {
          resolve(accessToken);
        }
      },
      {
        scope: "pages_show_list,business_management,instagram_basic,instagram_manage_insights",
      },
    );
  });
}

function InstagramRequirements({ className }: { className?: string }) {
  const { open } = useModal();

  return (
    <Typography asChild size="paragraphXs" weight={600} className={cn("text-left", className)}>
      <button
        type="button"
        className="underline"
        onClick={() => {
          open({
            title: "Requirements",
            className: "max-w-[480px] gap-3",
            titleClassName: "text-sm !font-semibold !font-inter",
            children: (
              <div className="space-y-4">
                <Typography size="paragraphXs" color="textSecondary">
                  Please ensure that you meet all of the connection requirements before connecting with
                  Instagram/Facebook.
                  <Typography size="paragraphXs" color="textSecondary" asChild>
                    <ol className="list-decimal pl-4">
                      <li>
                        A published Facebook Page (this is different than your Facebook profile).
                        <br />👉{" "}
                        <ExternalLink
                          className="text-colorBlue underline"
                          to="https://www.facebook.com/business/help/1199464373557428?id=418112142508425"
                        >
                          How to create a new Page on Facebook
                        </ExternalLink>
                      </li>
                      <li>
                        Next, you will need an Instagram professional account.
                        <br />👉{" "}
                        <ExternalLink
                          className="text-colorBlue underline"
                          to="https://help.instagram.com/2358103564437429"
                        >
                          How to set up a professional creator account on Instagram
                        </ExternalLink>
                      </li>
                      <li>
                        Finally, you will need to link your Facebook Page and Instagram professional accounts.
                        <br />👉{" "}
                        <ExternalLink
                          className="text-colorBlue underline"
                          to="https://www.facebook.com/business/help/898752960195806"
                        >
                          How to link your Facebook Page and Instagram professional accounts
                        </ExternalLink>
                      </li>
                    </ol>
                  </Typography>
                </Typography>
              </div>
            ),
          });
        }}
      >
        Requirements
      </button>
    </Typography>
  );
}

function YoutubeSection({ className }: { className?: string }) {
  return (
    <ChannelsPage.ConnectSection
      className={className}
      title="Youtube"
      description="Connect your Youtube channel to Backpac"
      icon={getAssetsUrl("sns/youtube.png")}
      subDescription={<YoutubeChannelApprovalRequirements />}
      rightAddon={<div />}
    >
      <ChannelTable className="mt-8" />
    </ChannelsPage.ConnectSection>
  );
}

function YoutubeChannelApprovalRequirements({ className }: { className?: string }) {
  const { open } = useModal();

  return (
    <Typography asChild size="paragraphXs" weight={600} className={cn("text-left", className)}>
      <button
        type="button"
        className="underline"
        onClick={() => {
          open({
            title: "Channel approval requirements",
            className: "max-w-[480px] gap-3",
            titleClassName: "text-sm !font-semibold !font-inter",
            children: (
              <Typography size="paragraphXs" color="textMuted">
                If your channel doesn&apos;t meet all the requirements, it will be rejected, and you won&apos;t have
                access to analytics or payouts.
                <List>
                  <ListItem>
                    All uploaded content must comply with YouTube&apos;s policies (e.g., no explicit or violent
                    material).
                  </ListItem>
                  <ListItem>Content must be original or comply with the U.S. copyright fair use doctrine.</ListItem>
                  <ListItem>The channel must be owned or managed by you.</ListItem>
                  <ListItem>Your channel must have at least 10 Shorts videos uploaded.</ListItem>
                </List>
              </Typography>
            ),
          });
        }}
      >
        Channel approval requirements
      </button>
    </Typography>
  );
}

interface ConnectSectionProps {
  title: string;
  description: string;
  icon: string;
  subDescription?: ReactNode;
  rightAddon?: ReactNode;
  children?: ReactNode;
  className?: string;
}

ChannelsPage.ConnectSection = function ConnectSection({
  title,
  description,
  icon,
  subDescription,
  rightAddon,
  children,
  className,
}: ConnectSectionProps) {
  return (
    <ShadowBox className={cn("p-8 text-left", className)}>
      <div className="flex flex-col desktop:flex-row desktop:justify-between gap-4">
        <div className="flex items-center gap-4">
          <img src={icon} alt={`${title} logo`} className="size-12 rounded-full" />
          <div className="flex flex-col">
            <Typography size="xs" weight={600}>
              {title}
            </Typography>
            <Typography size="paragraphXs" color="textMuted" className={subDescription ? "mb-[10px]" : ""}>
              {description}
            </Typography>
            {subDescription}
          </div>
        </div>
        <div className="flex justify-end">{rightAddon}</div>
      </div>
      {children}
    </ShadowBox>
  );
};
